import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import campaignSubmittedIllustration from '@features/campaigns/thematicPackage/assets/images/campaign-submitted.svg';
import { FieldBudgetTier } from '@features/campaigns/thematicPackage/components/FormFields/FieldBudgetTier/FieldBudgetTier.tsx';
import { FieldCustomBudgetAmount } from '@features/campaigns/thematicPackage/components/FormFields/FieldCustomBudgetAmount/FieldCustomBudgetAmount.tsx';
import { FieldThematicPackage } from '@features/campaigns/thematicPackage/components/FormFields/FieldThematicPackage/FieldThematicPackage.tsx';
import { PageHeader } from '@features/campaigns/thematicPackage/components/PageHeader/PageHeader.tsx';
import { UpsellFooter } from '@features/campaigns/thematicPackage/components/UpsellFooter/UpsellFooter.tsx';
import { useThematicPackageUpsell } from '@features/campaigns/thematicPackage/hooks/useThematicPackageUpsell.ts';
import { Icon } from '@shared/components/Icon/Icon.tsx';
import { ICON_VARIANTS } from '@shared/components/Icon/IconMapper.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { formatNumberAsMoney } from '@shared/utils/moneyFormat.ts';

import styles from './ThematicPackageUpsellPage.module.scss';

function ThematicPackageUpsellPage() {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  const {
    form,
    isPackagesPhase,
    isUpsellEntryAllowed,
    budgetTiers,
    customBudgetBounds,
    lowestBudgetAmount,
    thematicPackages,
    areThematicPackagesLoading,
    isBudgetSelectionComplete,
    isCustomBudgetSelected,
    selectedThematicPackageId,
    isCreateThematicPackageCampaignPending,
    handleProceedToPackages,
    handleBackToBudget,
    handleCreateThematicPackageCampaign,
    handleSkipUpsell,
    handleCloseUpsell,
  } = useThematicPackageUpsell();

  if (!isUpsellEntryAllowed) {
    return null;
  }

  const primaryActionCallback = isPackagesPhase
    ? () => void handleCreateThematicPackageCampaign()
    : () => void handleProceedToPackages();

  return (
    <section>
      <PageHeader closeCallback={handleCloseUpsell} />

      <div className='content-wrapper'>
        <div className={styles.contentContainer}>
          <img className={styles.successIllustration} src={campaignSubmittedIllustration} alt='' />
          <h2 className={styles.successHeading}>{t('thematicPackage.successHeading')}</h2>

          <div className={styles.card}>
            <h3 className={styles.question}>{t('thematicPackage.question')}</h3>
            <p className={styles.subtitle}>{t('thematicPackage.subtitle')}</p>

            <hr className={styles.divider} />

            <div className={styles.packagesIntro}>
              <div className={styles.packagesIllustration}>
                <Icon variant={ICON_VARIANTS.thematicPackages} iconAltText={t('thematicPackage.intro.title')} />
              </div>
              <h4 className={styles.packagesTitle}>{t('thematicPackage.intro.title')}</h4>
              <p className={styles.packagesDescription}>{t('thematicPackage.intro.description')}</p>
            </div>

            <FormProvider {...form}>
              {isPackagesPhase ? (
                <FieldThematicPackage
                  thematicPackages={thematicPackages}
                  areThematicPackagesLoading={areThematicPackagesLoading}
                />
              ) : (
                <>
                  <p className={styles.budgetInstruction}>
                    {t('thematicPackage.budget.instruction', {
                      minimumAmount: formatNumberAsMoney(lowestBudgetAmount),
                    })}
                  </p>
                  <FieldBudgetTier budgetTiers={budgetTiers} />

                  {isCustomBudgetSelected && customBudgetBounds && (
                    <>
                      <hr className={styles.divider} />
                      <FieldCustomBudgetAmount customBudgetBounds={customBudgetBounds} />
                    </>
                  )}
                </>
              )}
            </FormProvider>

            <hr className={styles.divider} />

            <UpsellFooter
              isPackagesPhase={isPackagesPhase}
              secondaryActionCallback={isPackagesPhase ? handleBackToBudget : handleSkipUpsell}
              primaryActionCallback={primaryActionCallback}
              isPrimaryActionDisabled={isPackagesPhase ? !selectedThematicPackageId : !isBudgetSelectionComplete}
              isPrimaryActionLoading={isCreateThematicPackageCampaignPending}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default ThematicPackageUpsellPage;
